<svelte:options tag={null} />

<script lang="ts">
  import {
    select,
    scaleBand,
    scaleLinear,
    axisBottom,
    axisLeft,
    Selection,
    ScaleBand,
    ScaleLinear,
    NumberValue,
    stack,
    Series,
  } from 'd3';
  import { onMount } from 'svelte';
  import type { StackedBarData, BarMargin } from './bar-chart.types';
  import { EventHandler } from '../../utilities/EventHandler';

  type StackedData = Series<
    {
      [key: string]: number;
    },
    string
  >;

  export let data = '';
  $: parsedData = data ? (JSON.parse(data) as StackedBarData[]) : [];
  $: groups = parsedData.map((d: StackedBarData) => d.group);
  $: subgroups = parsedData[0]
    ? Object.keys(parsedData[0]).filter((d: string) => d !== 'group')
    : [];

  export let chartheight = '300';
  $: height = parseInt(chartheight, 10);

  export let backgroundcolor = '#fff';
  export let scalemax = 50;
  export let ticknumbers = 4;
  export let tickleftformat: (d: NumberValue, i: number) => string = (d) =>
    `${Number(d)}`;
  export let tickbottomformat: (d: NumberValue, i: number) => string = (d) =>
    `${Number(d)}`;

  export let margins: string;
  export let svgs: string;
  $: parsedSvgs = svgs ? (JSON.parse(svgs) as SVGElement[]) : [];

  export let colors: string;
  let parsedColor: Record<string, string>;
  $: parsedColor = colors ? (JSON.parse(colors) as Record<string, string>) : {};

  let _data: string;
  let chart: HTMLDivElement;
  let svg: Selection<SVGGElement, any, any, any>;
  let hasMounted = false;
  let tooltipData = { show: false, text: '', top: 0, left: 0, width: 0 };
  let eventHandler = new EventHandler();

  $: if (data.length > 0 && hasMounted) {
    if (!_data) {
      render();
      _data = data;
    } else if (data !== _data) {
      update();
      _data = data;
    }
  }

  function getParsedMargins(margins: string): BarMargin {
    if (margins) return JSON.parse(margins) as BarMargin;
    else
      return {
        top: 10,
        right: 40,
        bottom: 20,
        left: 60,
        xAxis: 20,
      } as BarMargin;
  }

  function getChartWidth() {
    let chartWidth = select(chart).style('width');
    if (chartWidth === '' || !chartWidth) {
      chartWidth = `${window.innerWidth / 2}`;
    }
    return parseInt(chartWidth);
  }

  function getAxes(width: number) {
    const x = scaleBand()
      .domain(groups)
      .range([
        0,
        width -
          (getParsedMargins(margins).left / 2 +
            getParsedMargins(margins).right / 2),
      ])
      .padding(0.2);
    const y = scaleLinear().domain([0, scalemax]).range([height, 0]);
    return { x, y };
  }

  function addRectandles(
    x: ScaleBand<string>,
    y: ScaleLinear<number, number, never>,
  ) {
    const stackedData = stack().keys(subgroups)(parsedData);

    svg
      .append('g')
      .selectAll('g')
      .data(stackedData)
      .enter()
      .append('g')
      .attr('fill', (d: StackedData) => parsedColor[d.key] || 'black')
      .attr('class', 'bar')
      .selectAll('rect')
      .data((d) => d)
      .enter()
      .append('rect')
      .attr('x', (d) => x(d.data.group.toString())!)
      .attr('y', (d) => y(d[1]))
      .attr('width', x.bandwidth())
      .attr('height', (d) => y(d[0]) - y(d[1]))
      .style('cursor', 'pointer')
      .on('mouseout', (_e: MouseEvent, chartEvent) => {
        const others = svg.selectAll('rect').filter(function (arc) {
          return arc != chartEvent;
        });
        others.style('opacity', 1);
        tooltipData.show = false;
      })
      .on('mouseover', (e: MouseEvent, chartEvent: any) => {
        const others = svg.selectAll('rect').filter(function (arc) {
          return arc != chartEvent;
        });
        others.style('opacity', 0.7);
        const { left, top, width } = (
          e.target as HTMLElement
        ).getBoundingClientRect();
        tooltipData = {
          show: true,
          text: chartEvent.data.customtooltip
            ? chartEvent.data.customtooltip
            : `${chartEvent[1] - chartEvent[0]}`,
          left: left,
          top: top - 50,
          width,
        };
      })
      .on('click', (_e: MouseEvent, chartEvent) => {
        eventHandler.dispatch('bar-clicked', chartEvent);
      });
  }

  function updateAxes(
    x: ScaleBand<string>,
    y: ScaleLinear<number, number, never>,
  ) {
    svg
      .select('.x-axis')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      .call((axisBottom(x) as any).tickFormat(tickbottomformat));
    svg
      .select('.y-axis')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      .call((axisLeft(y) as any).ticks(ticknumbers).tickFormat(tickleftformat));

    const width = getChartWidth();
    svg
      .selectAll('.y-axis .tick line')
      .attr('stroke', '#EEEDEA')
      .attr('stroke-width', '1px')
      .attr('x2', width);
  }

  function render() {
    const width = getChartWidth();
    const { x, y } = getAxes(width);

    svg = select<HTMLElement, any>(chart)
      .append('svg')
      .attr('width', width)
      .attr(
        'height',
        Number(height) +
          Number(getParsedMargins(margins).top) +
          Number(getParsedMargins(margins).bottom) +
          Number(getParsedMargins(margins).xAxis),
      )
      .attr('id', 'svg-chart')
      .append('g')
      .attr(
        'transform',
        `translate(${
          getParsedMargins(margins).left / 2 +
          getParsedMargins(margins).right / 2
        },${getParsedMargins(margins).top})`,
      );

    svg
      .append('g')
      .attr(
        'transform',
        `translate(0, ${
          Number(height) + Number(getParsedMargins(margins).xAxis)
        })`,
      )
      .call(axisBottom(x))
      .attr('class', 'x-axis')
      .attr('stroke-width', '0');

    svg
      .append('g')
      .call(axisLeft(y).ticks(ticknumbers).tickFormat(tickleftformat))
      .attr('class', 'y-axis')
      .attr('stroke-width', '0');

    addRectandles(x, y);
  }

  function update() {
    const width = getChartWidth();
    chart.querySelector('#svg-chart')?.setAttribute('width', `${width}px`);

    const { x, y } = getAxes(width);
    updateAxes(x, y);
    svg.selectAll('.bar').remove();
    addRectandles(x, y);
  }

  function resize() {
    if (_data) {
      const width = getChartWidth();
      chart.querySelector('#svg-chart')?.setAttribute('width', `${width}px`);

      const { x, y } = getAxes(width);
      updateAxes(x, y);

      const stackedData = stack().keys(subgroups)(parsedData);
      svg
        .selectAll('.bar')
        .data(stackedData)
        .selectAll('rect')
        .data((d) => d)
        .attr('x', (d) => x(d.data.group.toString())!)
        .attr('y', (d) => y(d[1]))
        .attr('width', x.bandwidth())
        .attr('height', (d) => y(d[0]) - y(d[1]));
    }
  }

  onMount(() => {
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(chart);
    hasMounted = true;
  });
</script>

<div bind:this={chart} style="background-color: {backgroundcolor};" />

{#if tooltipData.show}
  <div
    class="tooltip"
    style={`top: ${tooltipData.top}px; left: ${tooltipData.left}px; width: ${tooltipData.width}px;`}
  >
    {tooltipData.text}
  </div>
{/if}

<div class="legend">
  {#each subgroups as subgroup}
    <div class="legend__item">
      <svg class="legend__box">
        <rect x="0" y="0" width="24" height="16" fill={parsedColor[subgroup]} />
      </svg>
      <span class="legend__title">{subgroup}</span>
    </div>
  {/each}
</div>

<div style="height: 0; width: 0;" class="pattern">
  {#each parsedSvgs as svg}
    {@html svg}
  {/each}
</div>
<text />

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/_all-settings';

  .legend {
    margin-top: magic-unit-rem(1);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 magic-unit-rem(1.5);
    height: 100%;
    color: $color-grey-999;
    gap: magic-unit-rem(1);
    &__item {
      display: flex;
      justify-content: center;
      font-size: $size-font-04;
      align-items: center;
    }
    &__title {
      white-space: nowrap;
      margin: 0;
    }
    &__box {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: magic-unit-rem(0.25);
      height: 16px;
      width: 24px;
    }
  }

  .tooltip {
    height: auto;
    background-color: $color-grey-700;
    border-radius: 4px;
    color: $color-grey-000;
    text-align: center;
    padding: magic-unit-rem(0.5) 0;
    position: absolute;
  }

  .pattern {
    z-index: -1;
    position: relative;
  }

  text {
    font-family: 'LeroyMerlin', sans-serif;
    font-size: $size-font-04;
    color: $color-grey-700;
  }
</style>
